import { IconButtonRegular as IconButton } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonRegular';
import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { PageErrorState } from '@components/core-ui/PageErrorState';
import { PagePagination } from '@components/core-ui/Pagination';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { SortableTh, SortingDirection, Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { TagAccent } from '@components/core-ui/Tag/variations/TagAccent';
import { Tooltip } from '@components/core-ui/Tooltip';
import { Header } from '@components/Header';
import ASOneIcon from '@components/icons/ASOne.svg';
import ChevronLeft from '@components/icons/ChevronLeft.svg';
import HistoryIcon from '@components/icons/History.svg';
import LockedJob from '@components/icons/LockedJob.svg';
import PencilIcon from '@components/icons/Pencil.svg';
import { JobStatusText } from '@components/JobStatusText';
import { downloadCSV } from '@helpers/downloadCSV';
import { formatDateMsDsYHI } from '@helpers/formatDateMsDsYHI';
import { formatIsoDateToAmerican } from '@helpers/formatIsoDateToAmerican';
import { getJobsListErrorMessage } from '@helpers/getJobsListErrorMessage';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { minutesToSeconds } from '@helpers/minutesToSeconds';
import { toUnixSecondsQueryParam } from '@helpers/toUnixSecondsQueryParam';
import { jobCanBeReassigned } from '@helpers/jobStatus/jobCanBeReassigned';
import { jobIsScheduled } from '@helpers/jobStatus/jobIsScheduled';
import { WSUpdateReceiver } from '@pages/User/JobsListPage/components/Content/components/WSUpdateReceiver';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { routes } from '@routes';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { JobResponseDto, OrganizationsService } from 'app/API';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { jobsKeys, TOrganizationJobsListParams } from 'app/queries/jobs/jobsKeys';
import { TPaginatedResponse } from 'app/types/API/TPaginatedResponse';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import React, { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { buildOrganizationJobsRequest } from './api/buildOrganizationJobsRequest';
import { createFetchOrganizationJobsQueryFn } from './api/fetchOrganizationJobs';
import { ALL_JOBS_CSV_HEADERS, buildAllJobsCsvRows } from './helpers/buildAllJobsCsvRows';
import { AssignCell } from './styles/AssignCell';
import { AssignLink } from './styles/AssignLink';
import { Content } from './styles/Content';
import { DeadlineCell } from './styles/DeadlineCell';
import { IconsWrapper } from './styles/IconsWrapper';
import { IdField } from './styles/IdField';
import { JobName } from './styles/JobName';
import { StatusCell } from './styles/StatusCell';
import { StatusCellContent } from './styles/StatusCellContent';
import { StatusDate } from './styles/StatusDate';
import { StyledLink } from './styles/StyledLink';
import { TagField } from './styles/TagField';
import { REPORTING_TYPE_ENUM } from 'app/types/enums/reportingTypes';
import { convertDuration } from '@helpers/convertDuration';
import { ButtonsTRow } from '@pages/User/JobsListPage/components/Content/components/JobsTable/ButtonsTRow';
import { canShowButtonsTRow } from '@pages/User/JobsListPage/components/Content/components/JobsTable/helpers/canShowButtonsTRow';
import { buildJobsFiltersSearchParams } from '@helpers/buildJobsFiltersSearchParams';
import { hasActiveJobsFilters } from '@helpers/hasActiveJobsFilters';
import { parseJobsFiltersFromSearchParams } from '@helpers/parseJobsFiltersFromSearchParams';
import { TAppliedFilters } from 'app/types/TAppliedFilters';

const SEARCH_DEBOUNCE_MS = 300;
const EMPTY_JOBS: TPaginatedResponse<JobResponseDto> = { data: [], count: 0 };

export const AllJobsPage = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { organization, me } = useAuthContext();
  const organizationId = organization?.id ?? '';
  const { openModal, closeModal } = useModalContext();
  const {
    searchParams,
    page,
    pageSize,
    search,
    setSearch,
    orderBy,
    orderType,
    setPage,
    setPageSize,
    toggleSort,
    updateParams,
  } = useListQueryParams({
    defaults: { pageSize: 25, orderBy: 'createdAt', orderType: 'desc' },
  });
  const [hiddenButtonsVisible, setHiddenButtonsVisible] = useState<string | null>(null);
  const filters = useMemo(() => parseJobsFiltersFromSearchParams(searchParams), [searchParams]);
  const { jobsFilter, statusFilter, asOneFilter, postFilter, meetingTypeFilter, durationFilter } = filters;
  const sortingField = orderBy;
  const sortingReverse = orderType === 'desc';

  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;
  const statuses = statusFilter.filter((v) => v.length).join(',');
  const jobsFrom = toUnixSecondsQueryParam(jobsFilter.from);
  const jobsTo = toUnixSecondsQueryParam(jobsFilter.to);
  const durationFrom = minutesToSeconds(durationFilter.fromMinutes);
  const durationTo = minutesToSeconds(durationFilter.toMinutes);
  const listParams = useMemo<TOrganizationJobsListParams>(
    () => ({
      page,
      pageSize,
      orderBy,
      orderType,
      search: debouncedSearch,
      statuses,
      jobsFrom,
      jobsTo,
      asOne: asOneFilter,
      post: postFilter,
      meetingTypes: meetingTypeFilter,
      durationFrom,
      durationTo,
    }),
    [
      asOneFilter,
      debouncedSearch,
      durationFrom,
      durationTo,
      jobsFrom,
      jobsTo,
      meetingTypeFilter,
      orderBy,
      orderType,
      page,
      pageSize,
      postFilter,
      statuses,
    ],
  );
  const listKey = useMemo(() => jobsKeys.orgList(organizationId, listParams), [organizationId, listParams]);

  const jobsQuery = useQuery({
    queryKey: listKey,
    queryFn: createFetchOrganizationJobsQueryFn(call),
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[0].organizationId === organizationId ? previousData : undefined,
    enabled: organizationId !== '' && !isSearchSettling,
  });
  const jobs = jobsQuery.data ?? EMPTY_JOBS;

  const setJobs = useCallback(
    (next: TPaginatedResponse<JobResponseDto>) =>
      queryClient.setQueryData<TPaginatedResponse<JobResponseDto>>(listKey, (current) => (current ? next : current)),
    [listKey, queryClient],
  );

  const reload = useCallback(
    () => queryClient.invalidateQueries({ queryKey: jobsKeys.listsByOrg(organizationId) }),
    [organizationId, queryClient],
  );

  const isVsAdmin = me?.roles?.includes('ROLE_VS_ADMIN');

  const canManageWorkspaces = !!organization?.permissions?.manageWorkspaces;

  const applyModalFilters = useCallback(
    (appliedFilters: TAppliedFilters) => {
      updateParams(buildJobsFiltersSearchParams(appliedFilters), { resetPage: true });
    },
    [updateParams],
  );

  const openFiltersModal = () => {
    openModal(ModalNamesEnum.AllJobsFilters, {
      jobsFilter,
      statusFilter,
      asOneFilter,
      postFilter,
      meetingTypeFilter,
      durationFilter,
      onApplyFilters: applyModalFilters,
    });
  };

  const editDeadline = (job: JobResponseDto) => {
    openModal(ModalNamesEnum.EditDeadline, {
      onSuccess: () => {
        reload();
        closeModal();
      },
      job,
    });
  };

  const editAssignee = (job: JobResponseDto) => {
    openModal(ModalNamesEnum.AssignJobModal, {
      onSuccess: () => {
        reload();
        closeModal();
      },
      job,
    });
  };

  const handleShowHistory = (job: JobResponseDto) => {
    openModal(ModalNamesEnum.JobHistory, {
      jobId: job.id,
      workspaceId: job.workspaceId,
    });
  };

  const exportMutation = useMutation({
    mutationFn: () =>
      call(() =>
        OrganizationsService.getAllOrganizationJobs(
          buildOrganizationJobsRequest(organizationId, { ...listParams, page: 1, pageSize: jobs.count }),
        ),
      ),
    onSuccess: (response) =>
      downloadCSV(buildAllJobsCsvRows(response.data, organization?.name ?? ''), ALL_JOBS_CSV_HEADERS),
    onError: notifyApiCallError,
  });

  const isLoading = jobsQuery.isFetching || isSearchSettling || exportMutation.isPending;
  const isExportDisabled =
    isSearchSettling || jobsQuery.isPlaceholderData || !jobsQuery.isSuccess || exportMutation.isPending;

  return (
    <>
      <Helmet>
        <title>All Jobs - AutoScript</title>
      </Helmet>
      <Header
        title="All Jobs"
        globalSearch={true}
        buttonLabel={jobs?.data?.length ? 'Export CSV' : undefined}
        buttonOnClick={jobs?.data?.length ? () => exportMutation.mutate() : undefined}
        buttonDisabled={isExportDisabled}
        buttonLoading={exportMutation.isPending}
        onFilterClick={openFiltersModal}
        filterCounter={hasActiveJobsFilters(filters) ? 1 : 0}
        setSearch={setSearch}
        search={search}
      />

      <LinearProgressLoader active={isLoading} />
      <Content>
        {jobsQuery.isError ? (
          <PageErrorState
            message={getJobsListErrorMessage(jobsQuery.error)}
            actionLabel="Retry"
            actionDisabled={jobsQuery.isFetching}
            onAction={() => void jobsQuery.refetch()}
          />
        ) : null}
        <TableWrapper>
          <Table>
            <Thead>
              <Tr>
                <SortableTh onClick={() => toggleSort('name')}>
                  Job Title
                  {sortingField === 'name' && <SortingDirection reverse={sortingReverse} />}
                </SortableTh>
                <SortableTh style={{ width: '1%' }} onClick={() => toggleSort('deadline')}>
                  Due
                  {sortingField === 'deadline' && <SortingDirection reverse={sortingReverse} />}
                </SortableTh>
                <SortableTh style={{ width: '20%' }} onClick={() => toggleSort('assigneeEmail')}>
                  ASSIGNEE
                  {sortingField === 'assigneeEmail' && <SortingDirection reverse={sortingReverse} />}
                </SortableTh>
                <SortableTh style={{ width: '1%' }} onClick={() => toggleSort('createdAt')}>
                  Created
                  {sortingField === 'createdAt' ? <SortingDirection reverse={sortingReverse} /> : null}
                </SortableTh>
                <SortableTh style={{ width: '1%' }} onClick={() => toggleSort('duration')}>
                  Duration
                  {sortingField === 'duration' ? <SortingDirection reverse={sortingReverse} /> : null}
                </SortableTh>
                <SortableTh style={{ width: '1%' }} onClick={() => toggleSort('status')}>
                  Status
                  {sortingField === 'status' ? <SortingDirection reverse={sortingReverse} /> : null}
                </SortableTh>
                <SortableTh style={{ width: '20%' }} onClick={() => toggleSort('workspaceName')}>
                  Workspace
                  {sortingField === 'workspaceName' && <SortingDirection reverse={sortingReverse} />}
                </SortableTh>
                <Th style={{ width: '1%' }} />
              </Tr>
            </Thead>
            <Tbody>
              {jobs?.data?.map((job: any, index: number) => {
                const showEditAssigneeButton =
                  organization?.permissions?.reassignJobs && jobCanBeReassigned(job.status);
                const canViewAllJobsHistory = organization?.permissions?.viewJobs === 'all';
                const showHistory = canViewAllJobsHistory || me?.id === job.assignee?.id;
                const cellClass = showHistory ? 'with-history' : '';
                const showASOneButton = !job.asOneLocked && jobIsScheduled(job.status);
                const getASOneLinkPrefix = () => {
                  return `${window.location.origin}/as-one/job/`;
                };
                const unlockJob = (id: string, name: string, partialUnlocked: boolean) => {
                  openModal(ModalNamesEnum.UnlockJob, {
                    onCancel: closeModal,
                    onSuccess: () => {
                      closeModal();
                      reload();
                    },
                    isSubmitting: false,
                    id,
                    name,
                    partialUnlocked,
                  });
                };

                return (
                  <React.Fragment key={job.id ?? index}>
                    <Tr>
                      <Td>
                        <div>
                          <div className="flex gap-3 items-center max-w-full">
                            {job.asOneLocked ? <LockedJob className="flex-shrink-0 pl-2" width="30" /> : null}
                            <div className="max-w-full">
                              <JobName className="max-w-full" data-testid={`jobTitle${job.name}`}>
                                {job.asOneLocked ? (
                                  job.name || 'Unnamed'
                                ) : (
                                  <div className="flex flex-row max-w-full">
                                    {job.meetingId ? <TagAccent className="mr-2 text-[10px]">AS ONE</TagAccent> : null}
                                    <StyledLink
                                      className="max-w-full text-ellipsis overflow-hidden"
                                      to={routes.jobDetails.make(organization?.slug ?? '', job.workspaceSlug, job.id)}
                                    >
                                      {job.name || 'Unnamed'}
                                    </StyledLink>
                                  </div>
                                )}
                              </JobName>

                              <IdField>{job.id}</IdField>
                              {job.noShow && <TagField>#no_show</TagField>}
                            </div>
                          </div>
                        </div>
                      </Td>
                      <Td>
                        <DeadlineCell>
                          {!job.deadline && <p className="deadLine"></p>}
                          {job.deadline && <p className="deadLine">{formatIsoDateToAmerican(job.deadline)}</p>}
                          {organization?.permissions?.submitJobs ? (
                            <PencilIcon
                              className="text-[var(--color-text-muted)] hover:text-[var(--color-accent)] cursor-pointer"
                              onClick={() => editDeadline(job)}
                            />
                          ) : null}
                        </DeadlineCell>
                      </Td>
                      <Td>
                        <AssignCell>
                          {!job?.assignee ? <div className="assignee"></div> : null}
                          {job?.assignee ? (
                            <Tooltip key={index} content={job?.assignee?.email || ''} placement="bottom">
                              <div className="assignee">
                                <AssignLink to={routes.userDetails.make(organization?.slug ?? '', job?.assignee?.id)}>
                                  <p className="assignName">
                                    {job?.assignee?.name} {job?.assignee?.lastname}
                                  </p>
                                  <p className="assignEmail">{job?.assignee?.email || ''}</p>
                                </AssignLink>
                              </div>
                            </Tooltip>
                          ) : null}
                          {showEditAssigneeButton ? (
                            <PencilIcon
                              className="text-[var(--color-text-muted)] hover:text-[var(--color-accent)] cursor-pointer"
                              onClick={() => editAssignee(job)}
                            />
                          ) : null}
                        </AssignCell>
                      </Td>
                      <Td>
                        <p>{getAmericanTime(job.createdAt)}</p>
                      </Td>
                      <Td>
                        <p>
                          {job.reportingType === REPORTING_TYPE_ENUM.REPORTING_TYPE_FTR
                            ? 'To be analyzed'
                            : convertDuration(Number(job.duration))}
                        </p>
                      </Td>
                      <Td>
                        <StatusCell className={cellClass}>
                          <StatusCellContent>
                            <JobStatusText testid={`jobStatus${job.name}`} status={job.status} removed={job.removed} />
                            <StatusDate>{formatDateMsDsYHI(job.statusChangeDate ?? '')}</StatusDate>
                          </StatusCellContent>
                          {showHistory ? (
                            <IconButtonClean onClick={() => handleShowHistory(job)}>
                              <HistoryIcon />
                            </IconButtonClean>
                          ) : null}
                        </StatusCell>
                      </Td>
                      <Td>
                        <p>{job.workspaceName}</p>
                      </Td>
                      <Td style={{ width: '1%' }}>
                        <IconsWrapper>
                          {job.asOneLocked && job.status === 'JOB_STATUS_LOCKED' ? (
                            <OutlinedButton
                              className="unlock-button"
                              onClick={() => unlockJob(job.id, job.name, Boolean(job.partialUnlocked))}
                            >
                              Unlock now
                            </OutlinedButton>
                          ) : null}
                          {showASOneButton ? (
                            <IconButton
                              title="Open in AutoScriptOne"
                              onClick={() =>
                                window.open(`${getASOneLinkPrefix()}${job.meetingId}`, '_blank', 'noopener,noreferrer')
                              }
                            >
                              <ASOneIcon />
                            </IconButton>
                          ) : null}
                          {!job.asOneLocked &&
                          canShowButtonsTRow(job, isVsAdmin, canManageWorkspaces, organization, me) ? (
                            <>
                              <IconButton
                                title="Open additional actions"
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                  e.preventDefault();
                                  setHiddenButtonsVisible(hiddenButtonsVisible === job.id ? null : job.id);
                                }}
                              >
                                <ChevronLeft
                                  className={`transition-all ${hiddenButtonsVisible === job.id ? 'rotate-90' : '-rotate-90'}`}
                                />
                              </IconButton>
                            </>
                          ) : null}
                        </IconsWrapper>
                      </Td>
                    </Tr>

                    {hiddenButtonsVisible === job.id ? (
                      <ButtonsTRow job={job} reload={reload} skipFirstRow={true} />
                    ) : null}
                  </React.Fragment>
                );
              })}
            </Tbody>
          </Table>
        </TableWrapper>
        <WSUpdateReceiver jobs={jobs} setJobs={setJobs} organizationId={organization?.id ?? ''} reload={reload} />
        <PagePagination
          totalItemsCount={jobs?.count ?? 0}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </Content>
    </>
  );
};
